import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { colors } from '../../../utils/theme';
import RevealOnScroll from '../../../utils/RevealOnScroll';
import redbullLogo from '../../../images/FeaturedProjects/Redbull/RedBull.png';
import { BsChevronDown } from 'react-icons/bs';

const ProjectContainer = styled.div`
  min-height: 100vh;
  padding: 2rem;
  background-color: ${colors.backgroundColor};
  color: ${colors.textColor};
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6rem 0;
  text-align: center;
`;

const Logo = styled.img`
  width: 400px;
  height: auto;
  
  @media (max-width: 768px) {
    width: 300px;
  }
`;

const Subtitle = styled.h2`
  font-size: clamp(1.2rem, 2vw, 1.5rem);
  color: ${colors.subtitleColor};
  margin-top: 2rem;
  font-family: "maitree", serif;
  max-width: 600px;
  text-align: left;
  font-weight: 400;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    margin-top: 1rem;
  }
`;

const ContentSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Description = styled.p`
  font-size: 1.4rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  color: ${colors.subtitleColor};
`;

const InstagramFeed = styled.div`
  width: 100%;
  padding: 4rem 0;
  background-color: ${colors.backgroundColor};
  overflow: hidden;
`;

const InstagramGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const InstagramPost = styled.div`
  width: 100%;
  min-height: 400px;
  background: ${colors.backgroundColor};
  border-radius: 8px;
  overflow: hidden;

  iframe {
    width: 100% !important;
    margin: 0 !important;
  }
`;

const ArrowContainer = styled(motion.div)`
  margin-top: 15rem;
  color: white;
  opacity: 0.3;
  font-size: 1.5rem;
`;

const Redbull = () => {
  const instagramPosts = [
    'https://www.instagram.com/reel/DAjuYVBuIM2/?utm_source=ig_embed&amp;utm_campaign=loading',
    "https://www.instagram.com/p/DDab5nKSq3B/",
    "https://www.instagram.com/p/DChWgDSPirK/"
  ];

  useEffect(() => {
    // Load Instagram embed script
    const script = document.createElement('script');
    script.src = 'https://www.instagram.com/embed.js';
    script.async = true;
    document.body.appendChild(script);

    // Initialize embeds when script loads
    script.onload = () => {
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []); // Initial script load

  // Re-process embeds when posts change
  useEffect(() => {
    if (window.instgrm) {
      window.instgrm.Embeds.process();
    }
  }, [instagramPosts]);

  const arrowVariants = {
    animate: {
      y: [0, 10, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <ProjectContainer>
      <HeaderSection>
        <Logo src={redbullLogo} alt="Red Bull Logo" />
        <Subtitle>Red Bull gives you snow — crafting dynamic content that captures the energy, excitement, and innovation of campus events.</Subtitle>
        <ArrowContainer
          variants={arrowVariants}
          animate="animate"
        >
          <BsChevronDown />
        </ArrowContainer>
      </HeaderSection>

      <InstagramFeed>
        <InstagramGrid>
          {instagramPosts.map((post, index) => (
            <RevealOnScroll key={index}>
              <InstagramPost>
                <blockquote
                  className="instagram-media"
                  data-instgrm-permalink={post}
                  data-instgrm-version="14"
                  style={{ 
                    background: '#FFF',
                    border: '0',
                    borderRadius: '3px',
                    boxShadow: 'none',
                    margin: '1px',
                    maxWidth: '540px',
                    minWidth: '326px',
                    padding: '0',
                    width: '99.375%'
                  }}
                />
              </InstagramPost>
            </RevealOnScroll>
          ))}
        </InstagramGrid>
      </InstagramFeed>

      <ContentSection>
        <RevealOnScroll>
          <Description>
            Led content creation and event coverage for Red Bull's campus initiatives, delivering high-energy videos and photos that captured the spirit of student life. From major events to intimate gatherings, I created content that amplified Red Bull's brand message while staying true to authentic campus culture.
          </Description>
        </RevealOnScroll>
      </ContentSection>
    </ProjectContainer>
  );
};

export default Redbull;
